import { gql } from 'graphql-tag';
import { TypedDocumentNode } from '@apollo/client';

import {
  GetCatchDetails,
  GetCatchDetailsVariables,
  GetCatchPostComments,
  GetCatchPostCommentsVariables,
} from '@generated/graphql';
import { BrandFragments } from '@graphql/fragments/rutilus/BrandType';
import { CatchFragments } from '@graphql/fragments/rutilus/CatchType';
import { FishingMethodFragments } from '@graphql/fragments/rutilus/FishingMethodType';
import { FishingWaterFragments } from '@graphql/fragments/rutilus/FishingWaterType';
import { ImageFragments } from '@graphql/fragments/rutilus/ImageType';
import { PostFragments } from '@graphql/fragments/rutilus/PostType';
import { ProductFragments } from '@graphql/fragments/rutilus/ProductType';
import { ProductUnitFragments } from '@graphql/fragments/rutilus/ProductUnitType';
import { SpeciesFragments } from '@graphql/fragments/rutilus/SpeciesTypes';
import { UserFragments } from '@graphql/fragments/rutilus/UserType';
import { WeatherAndMarineReadingFragments } from '@graphql/fragments/rutilus/WeatherAndMarineReadingType';

export const getCatchPostComments: TypedDocumentNode<
  GetCatchPostComments,
  GetCatchPostCommentsVariables
> = gql`
  query GetCatchPostComments($externalId: String, $first: Int) {
    post(externalId: $externalId) {
      ...PostId
      comments(first: $first) {
        totalCount
        edges {
          cursor
          node {
            ...PostId
            createdAt
            likedByCurrentUser
            likesCount
            text {
              text
            }
            user {
              ...UserAvatar
              ...UserId
              nickname
            }
            comments(first: 20) {
              edges {
                node {
                  ...PostId
                  createdAt
                  likedByCurrentUser
                  likesCount
                  user {
                    ...UserAvatar
                    ...UserId
                    nickname
                  }
                  text {
                    text
                  }
                }
              }
              totalCount
            }
          }
        }
      }
      likedByCurrentUser
      likesCount
    }
  }
  ${PostFragments.id}
  ${UserFragments.avatar}
  ${UserFragments.id}
`;

export const getCatchDetails: TypedDocumentNode<GetCatchDetails, GetCatchDetailsVariables> = gql`
  query GetCatchDetails($externalId: String) {
    catchDetails: post(externalId: $externalId) {
      ...PostId
      catchConditions: catch {
        ...CatchId
        caughtAtLocalTimeZone
        latitude
        longitude
        moonIllumination
        sunPosition
        weatherAndMarineReading {
          ...WeatherAndMarineReadingId
          airPressure
          airTemperature
          waterTemperature
          weatherCondition {
            localizedValue
            worldWeatherOnlineIdentifier
          }
          windDirection {
            degrees
            shortLocalizedValue
          }
          windSpeed
        }
      }
      catchPost: catch {
        ...CatchId
        catchAndRelease
        caughtAtGmt
        createdAt
        fishingMethod {
          ...FishingMethodId
          displayName
        }
        fishingWater {
          ...FishingWaterId
          displayName
          latitude
          longitude
        }
        hasExactPosition
        length
        locationPrivacy
        species {
          ...SpeciesId
          displayName
        }
        user {
          ...UserId
          avatar {
            url
          }
          nickname
        }
        weight
      }
      trip {
        id
        catches {
          totalCount
        }
      }
      catchGear: displayProductUnits(first: 20) {
        edges {
          node {
            ...ProductUnitId
            product {
              ...ProductId
              brand {
                ...BrandId
                name
              }
              image {
                ...ImageFields
              }
              displayName
            }
          }
        }
      }
    }
  }
  ${BrandFragments.id}
  ${CatchFragments.id}
  ${FishingMethodFragments.id}
  ${FishingWaterFragments.id}
  ${ImageFragments.imageFields}
  ${SpeciesFragments.id}
  ${PostFragments.id}
  ${ProductFragments.id}
  ${ProductUnitFragments.id}
  ${UserFragments.id}
  ${WeatherAndMarineReadingFragments.id}
`;
